/**
 * API client.
 *
 * The SPA is served from the same origin as the API, so requests are relative
 * and the session cookie travels automatically. `credentials: 'include'` is set
 * anyway so the client keeps working if it is ever hosted separately.
 *
 * Errors arrive in the server's envelope; `ApiError` preserves the machine
 * readable `code` and the `requestId`, which is what makes a support
 * conversation possible.
 */

export interface ApiErrorDetail {
  path?: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: ApiErrorDetail[];

  constructor(
    status: number,
    code: string,
    message: string,
    requestId: string | null,
    details: ApiErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; requestId?: string; details?: ApiErrorDetail[] };
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'include',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Could not reach the server.', null);
  }

  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const envelope = (parsed ?? {}) as ErrorEnvelope;
    throw new ApiError(
      response.status,
      envelope.error?.code ?? 'UNKNOWN',
      envelope.error?.message ?? `Request failed with status ${String(response.status)}`,
      envelope.error?.requestId ?? response.headers.get('x-request-id'),
      envelope.error?.details ?? [],
    );
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

/* ------------------------------------------------------------------- types */
/* Mirrors the server's response shapes. Money is always an integer in the     */
/* currency's minor unit.                                                      */

export type Role = 'owner' | 'admin' | 'member';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  role: Role;
}

export interface Member {
  id: string;
  role: Role;
  createdAt: string;
  user: { id: string; name: string; email: string; image: string | null };
}

export interface Invitation {
  id: string;
  email: string;
  role: Role;
  status: string;
  expiresAt: string;
  /**
   * Built by the server from its own APP_URL, not from `window.location`: the
   * link has to work in whatever the inviter pastes it into, which may not be
   * the origin they happen to be browsing.
   */
  acceptUrl: string;
}

/** An entry in the service catalog. Public: `GET /api/services`. */
export interface Service {
  key: string;
  name: string;
  description: string | null;
  kind: 'metered' | 'feature';
  unit: string;
  aggregation: string;
}

export interface PlanService {
  serviceKey: string;
  name: string;
  unit: string;
  kind: 'metered' | 'feature';
  enabled: boolean;
  includedQuantity: number | null;
  overagePriceMinor: number | null;
  overageUnitSize: number;
}

export interface Plan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  interval: 'month' | 'year';
  currency: string;
  basePriceMinor: number;
  version: number;
  contactSalesOnly: boolean;
  services: PlanService[];
}

export type SubscriptionStatus =
  'incomplete' | 'trialing' | 'active' | 'past_due' | 'paused' | 'canceled';

export interface Subscription {
  id: string;
  planId: string;
  planSlug: string | null;
  status: SubscriptionStatus;
  currency: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  provider: string;
}

export interface Entitlement {
  serviceKey: string;
  name: string;
  kind: 'metered' | 'feature';
  unit: string;
  enabled: boolean;
  limit: number | null;
  overagePriceMinor: number | null;
  overageUnitSize: number;
  overridden: boolean;
}

export interface UsageEntry {
  serviceKey: string;
  name: string;
  kind: 'metered' | 'feature';
  unit: string;
  quantity: number;
  eventCount: number;
  limit: number | null;
  overageQuantity: number;
  enabled: boolean;
}

/**
 * A live session as the list and detail endpoints return it.
 *
 * `state` is derived by the server from the schedule and the provider's own
 * signals, so the client never computes it — a class that started late and is
 * overrunning still arrives as `live`.
 *
 * Provider references are deliberately absent: a join URL comes only from
 * POST …/join, which is where the server authorises and audits the join.
 */
export interface LiveSession {
  id: string;
  organizationId: string;
  title: string;
  description: string | null;
  kind: 'zoom' | 'youtube';
  status: 'draft' | 'scheduled' | 'canceled';
  state: LiveSessionState;
  startsAt: string;
  endsAt: string;
  timezone: string;
  durationMinutes: number;
  capacity: number | null;
  hostName: string | null;
  actualStartedAt: string | null;
  actualEndedAt: string | null;
  joinable: boolean;
  createdAt: string;
  updatedAt: string;

  /**
   * The repeating class this is one occurrence of, if any.
   *
   * Null for a one-off. Set for every occurrence a recurrence rule produced,
   * which is what lets a term be shown as one entry rather than ten.
   */
  seriesId: string | null;
}

export type LiveSessionState = 'upcoming' | 'live' | 'past' | 'canceled';

/** Flat, with nullable slots — the server avoids a union so clients stay simple. */
export interface JoinTicket {
  liveSessionId: string;
  kind: 'zoom' | 'youtube';
  role: 0 | 1;
  displayName: string;
  expiresAt: string | null;
  watchUrl: string | null;
  youtubeVideoId: string | null;
  joinUrl: string | null;
  meetingNumber: string | null;
  passcode: string | null;
  sdkKey: string | null;
  signature: string | null;
  zak: string | null;
}

/**
 * A code a publisher hands to a student, redeemed once for access to a class.
 */
export interface JoinCode {
  id: string;
  code: string;
  label: string;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  status: 'active' | 'revoked' | 'expired' | 'exhausted';
  /** Set when this code belongs to one student rather than to a room. */
  issuedToEmail: string | null;
  sentAt: string | null;
  redeemedAt: string | null;
  /** Who used it. For a code handed out by hand, the only record there is. */
  redeemedByEmail: string | null;
  createdAt: string;
}

export interface MeetingCredential {
  id: string;
  provider: string;
  label: string;
  active: boolean;
  /** Masked previews. Identifiers appear in full; secrets never do. */
  credentialHints: Record<string, string>;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestError: string | null;
  createdAt: string;
}

export interface Invoice {
  id: string;
  periodStart: string;
  periodEnd: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  currency: string;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  amountPaidMinor: number;
  issuedAt: string | null;
  paidAt: string | null;
}

export interface InvoiceItem {
  id: string;
  type: 'base' | 'included' | 'overage' | 'one_time' | 'credit' | 'discount' | 'tax';
  description: string;
  quantity: number;
  unitPriceMinor: number;
  unitSize: number;
  amountMinor: number;
  providerRef: string | null;
}

export interface BillingStatus {
  provider: string;
  capabilities: { customerPortal: boolean; usageReporting: boolean; nativeTrials: boolean };
  checkoutUrl: string | null;
  subscriptionStatus: string | null;
}

export interface InvoicePreview {
  periodStart: string;
  periodEnd: string;
  currency: string;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  lines: Array<{
    type: string;
    description: string;
    quantity: number;
    unitPriceMinor: number;
    unitSize: number;
    amountMinor: number;
  }>;
}
